import type {trpc} from "@/app/trpc";
import {BottomSheet} from "../layout";
import {TabGroup, TabPanel, TabPanels} from "@headlessui/react";
import {
  StickyHeader,
  Slider,
  YStack,
  Separator,
  XStack,
  Button,
  TabList,
  Tab,
  ManagedImage,
} from "@chayka/ui";
import {useNavigate} from "react-router-dom";
import {useAuth} from "@/hooks/useAuth";

interface ServiceVerboseBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  service: (typeof trpc.rooms.list)["~types"]["output"]["drums"][number];
}

export const ServiceVerboseBottomSheet: React.FC<ServiceVerboseBottomSheetProps> = ({
  isOpen,
  onClose,
  service,
}) => {
  const navigate = useNavigate();

  const handleBookingClick = () => {
    navigate(`/app/booking?roomId=${service.id}`);
  };

  const {isAuthenticated, user} = useAuth();

  return (
    <BottomSheet fullHeight isOpen={isOpen} onClose={onClose}>
      <YStack className="gap-6">
        <StickyHeader className="-mt-6 -top-6! z-1!" title={service.name} />
        <YStack className="gap-4">
          <Slider
            withDots
            items={service.images}
            renderItem={({item, isActive}) => (
              <ManagedImage src={item} alt={service.name} priority={isActive} />
            )}
            className="rounded-xl overflow-hidden"
          />

          <h1 className="text-2xl font-light text-center">{service.name}</h1>
          <Separator />

          <YStack className="gap-1">
            <div className="font-bold">Тип занятия:</div>
            <XStack className="gap-2 flex-wrap">
              {service.bookingTypes.map((type) => (
                <div
                  className="whitespace-nowrap text-sm border border-stroke px-2 py-1 rounded-full"
                  key={type}
                >
                  {type}
                </div>
              ))}
            </XStack>
          </YStack>

          <YStack className="gap-1">
            <div className="font-bold">Площадь: {service.square} м²</div>
            <div className="text-sm">
              Комфортно для {service.capacity} человек{service.capacity > 1 ? "" : "а"}
            </div>
          </YStack>

          <div className="font-bold text-xl">
            {service.isPriceMinimum && "от "}
            {service.price} ₽ в час
          </div>

          <Button
            onClick={handleBookingClick}
            disabled={!isAuthenticated || user?.confirmationStatus !== "approved"}
          >
            Забронировать
          </Button>
        </YStack>

        <Separator />

        <TabGroup className="flex flex-col gap-4">
          <TabList className="-mx-6 px-6">
            <Tab>Оборудование</Tab>
            <Tab>О комнате</Tab>
            <Tab>Важная информация</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <YStack className="gap-4">
                {service.equipment.map((item) => (
                  <YStack key={item.content.toString()} className="gap-1 text-sm">
                    <div className="font-bold">{item.type}</div>
                    {Array.isArray(item.content) ? (
                      item.content.map((contentItem) => <div key={contentItem}>{contentItem}</div>)
                    ) : (
                      <div>{item.content}</div>
                    )}
                  </YStack>
                ))}
              </YStack>
            </TabPanel>
            <TabPanel>
              <YStack className="gap-2">
                {service.about.map((paragraph) =>
                  Array.isArray(paragraph) ? (
                    <p key={paragraph.toString()} className="text-sm">
                      <span className="font-bold">{paragraph[0]}</span> –{" "}
                      <span>{paragraph[1]}</span>
                    </p>
                  ) : (
                    <p key={paragraph.toString()} className="text-sm">
                      {paragraph}
                    </p>
                  ),
                )}
              </YStack>
            </TabPanel>
            <TabPanel>
              <YStack className="gap-2">
                {service.important.map((paragraph) =>
                  typeof paragraph === "string" ? (
                    <p key={paragraph.toString()} className="text-sm">
                      {paragraph}
                    </p>
                  ) : Array.isArray(paragraph) ? (
                    <p key={paragraph.toString()} className="text-sm">
                      <span className="font-bold">{paragraph[0]}</span> –{" "}
                      <span>{paragraph[1]}</span>
                    </p>
                  ) : (
                    <p key={paragraph.toString()} className="text-sm">
                      <span className="font-bold">
                        {(paragraph as {header: string; items: string[]}).header}
                      </span>
                      :{" "}
                      {(paragraph as {header: string; items?: string[]}).items?.map((item) => (
                        <p key={item}>- {item}</p>
                      ))}
                    </p>
                  ),
                )}
              </YStack>
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </YStack>
    </BottomSheet>
  );
};
